// useReducer-based surface state: component map + data model

import type { A2UIComponentDefinition } from '../types/A2UIComponents.types.tsx';

import { createDataModel, type DataModel } from './dataModel.ts';

export type SurfaceState = {
	catalogId: string | null;
	componentMap: Map<string, A2UIComponentDefinition>;
	dataModel: DataModel;
	rootId: string | null;
	surfaceId: string | null;
};

export type SurfaceAction =
	| {
			catalogId: string;
			components: A2UIComponentDefinition[];
			dataModel?: Record<string, unknown>;
			rootId: string;
			surfaceId: string;
			type: 'CREATE_SURFACE';
	  }
	| {
			catalogId?: string;
			rootId: string;
			surfaceId: string;
			type: 'INIT_SURFACE';
	  }
	| {
			components: A2UIComponentDefinition[];
			type: 'UPDATE_COMPONENTS';
	  }
	| {
			operations: Array<{ op: 'add' | 'replace' | 'remove'; path: string; value?: unknown }>;
			type: 'UPDATE_DATA_MODEL';
	  }
	| {
			type: 'DELETE_SURFACE';
	  };

export function createInitialSurfaceState(): SurfaceState {
	return {
		surfaceId: null,
		catalogId: null,
		rootId: null,
		componentMap: new Map(),
		dataModel: createDataModel(),
	};
}

export function surfaceReducer(state: SurfaceState, action: SurfaceAction): SurfaceState {
	switch (action.type) {
		case 'CREATE_SURFACE': {
			const componentMap = new Map<string, A2UIComponentDefinition>();
			for (const component of action.components) {
				componentMap.set(component.id, component);
			}
			const dataModel = createDataModel(action.dataModel ?? {});
			return {
				surfaceId: action.surfaceId,
				catalogId: action.catalogId,
				rootId: action.rootId,
				componentMap,
				dataModel,
			};
		}
		case 'INIT_SURFACE': {
			// v0.8 beginRendering — set surface metadata without replacing componentMap or dataModel
			return {
				...state,
				surfaceId: action.surfaceId,
				catalogId: action.catalogId ?? state.catalogId,
				rootId: action.rootId,
			};
		}
		case 'UPDATE_COMPONENTS': {
			const componentMap = new Map(state.componentMap);
			for (const component of action.components) {
				componentMap.set(component.id, component);
			}
			return { ...state, componentMap };
		}
		case 'UPDATE_DATA_MODEL': {
			for (const op of action.operations) {
				if (op.op === 'remove') {
					state.dataModel.remove(op.path);
				} else {
					state.dataModel.set(op.path, op.value);
				}
			}
			// Return same state ref — dataModel is mutable, components re-render via subscriptions
			return state;
		}
		case 'DELETE_SURFACE': {
			return createInitialSurfaceState();
		}
		default:
			return state;
	}
}
